import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, GraduationCap, MapPin, Sparkles, Palette, Printer, Shirt, PenTool, Newspaper } from "lucide-react";
import MakebaLogo from "../components/MakebaLogo";

const services = [
  { icon: Palette, title: "Branding", desc: "Full identity systems - logos, color, type, brand guidelines." },
  { icon: PenTool, title: "Custom Illustrations", desc: "Original characters and hand-crafted illustration for products & brands." },
  { icon: Shirt, title: "Apparel & Merchandise", desc: "Cut-and-sew, screen print, embroidery - small runs to 10,000 units." },
  { icon: Printer, title: "Screen Printing & Embroidery", desc: "In-studio production with obsessive attention to color and stitch." },
  { icon: Newspaper, title: "Print & Digital Marketing", desc: "Flyers, posters, catalogs, decks, campaigns - designed to convert." },
  { icon: Sparkles, title: "Custom Graphic Design", desc: "Anything visual. Menus, packaging, event graphics, social." },
];

const About = () => {
  return (
    <main data-testid="about-page" className="bg-white">
      {/* HERO */}
      <section className="bg-[#FAFAF7] py-20 md:py-28">
        <div className="container-x grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#EC008C] font-semibold mb-4">About the studio</div>
            <h1 className="font-display font-light text-5xl md:text-7xl leading-[0.95] tracking-tight text-black">
              Design with <span className="font-black">intention.</span><br />
              Craft with <span className="font-black">purpose.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#4A4A4A] font-light leading-relaxed max-w-lg">
              MAKEBA Design &amp; Prints is a full-service creative studio based in Oklahoma -
              built on 20+ years of illustration, branding, screen print, and embroidery excellence.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">Start a project <ArrowRight size={14} /></Link>
              <Link to="/shop" className="btn-outline">Shop the collection</Link>
            </div>
          </div>
          <div className="aspect-[4/5] overflow-hidden bg-[#F0EEE9]">
            <img
              src="/brand/makeba.png"
              alt="Makeba Griffin"
              className="w-full h-full object-cover"
              style={{ objectPosition: "50% 0%", transform: "scale(1.55)", transformOrigin: "50% 20%" }}
            />
          </div>
        </div>
      </section>

      {/* MAKEBA STORY */}
      <section className="container-x py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">Meet the maker</div>
            <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight">
              Hi, I'm <span className="font-black">Makeba.</span>
            </h2>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex items-center gap-2"><MapPin size={16} className="text-[#EC008C]"/> Oklahoma-based · Mississippi roots</div>
              <div className="flex items-center gap-2"><GraduationCap size={16} className="text-[#EC008C]"/> M.S. Business Intelligence</div>
              <div className="flex items-center gap-2"><Award size={16} className="text-[#EC008C]"/> 2025 OKSBDC Small Business of the Year</div>
            </div>
          </div>
          <div className="md:col-span-8 md:pl-6">
            <p className="text-base md:text-lg text-[#4A4A4A] font-light leading-relaxed">
              I've spent more than <strong className="text-black font-semibold">twenty years in graphic design, illustration, branding, and custom apparel</strong> -
              with a Master's in Business Intelligence backing every decision. What started at a kitchen table has grown
              into a nationally recognized creative studio.
            </p>
            <p className="mt-4 text-base md:text-lg text-[#4A4A4A] font-light leading-relaxed">
              Our work has been featured by <strong className="text-black font-semibold">ESSENCE Magazine</strong>, and we're the
              <strong className="text-black font-semibold"> officially licensed collegiate vendor for Langston University</strong> -
              Oklahoma's only HBCU.
            </p>
            <p className="mt-4 text-base md:text-lg text-[#4A4A4A] font-light leading-relaxed">
              Every stitch, every print, every product carries the same intention we started with - beautiful design made
              with purpose, and made to last.
            </p>
            <blockquote className="mt-8 border-l-2 border-[#EC008C] pl-6 py-2">
              <div className="font-script text-2xl md:text-3xl leading-tight text-black">
                "Design isn't decoration. It's how we tell the world who we are."
              </div>
              <div className="mt-3 text-[11px] font-semibold tracking-[0.24em] uppercase text-[#4A4A4A]">- Makeba Griffin</div>
            </blockquote>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#111] text-white py-20 md:py-28">
        <div className="container-x">
          <div className="text-center mb-14">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#F3E30A] font-semibold mb-3">What we do</div>
            <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight">Studio <span className="font-black">services.</span></h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.title} className="border border-white/20 p-8">
                <s.icon size={28} className="text-[#EC008C] mb-4" />
                <h3 className="font-display font-bold text-xl mb-2">{s.title}</h3>
                <p className="text-sm font-light text-white/80 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#F3E30A] text-black px-8 py-3.5 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-[#EC008C] hover:text-white transition">
              Request a Quote <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* STEAM-2-GO */}
      <section data-testid="steam-2-go-section" className="bg-[#EC008C] text-white py-24 md:py-32">
        <div className="container-x grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#F3E30A] font-semibold mb-4">Community Initiative</div>
            <h2 className="font-display font-light text-5xl md:text-7xl leading-[0.95] tracking-tight">
              <span className="font-black">STEAM-2-GO.</span>
            </h2>
            <div className="mt-2 font-script text-3xl md:text-4xl text-[#F3E30A]">learn · explore · represent</div>
            <p className="mt-8 text-base md:text-lg font-light leading-relaxed max-w-lg text-white/95">
              We're the creator of <strong className="font-semibold">Oklahoma's first STEAM-2-GO kits</strong> - designed to inspire
              creativity, encourage exploration, and promote representation through hands-on learning experiences.
            </p>
            <p className="mt-4 text-base md:text-lg font-light leading-relaxed max-w-lg text-white/90">
              Every kit pairs a MAKEBA character with a real STEM activity - Malik the mathematician, Nia the scientist,
              Mason the astronomer, Harper the reader. Kids don't just learn. They see themselves in the story of science.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="stem2go-partner" className="inline-flex items-center gap-2 bg-white text-black px-8 py-3.5 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-black hover:text-white transition">
                Partner with us <ArrowRight size={14} />
              </Link>
              <Link to="/shop?category=backpacks" data-testid="stem2go-shop" className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-3.5 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-white hover:text-[#EC008C] transition">
                Shop the characters
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img src="/products/newcatalog/signature-backpack-nia-science-pink/1.jpg" alt="Nia the scientist" className="w-full aspect-[4/5] object-cover" />
            <img src="/products/newcatalog/signature-backpack-mason-astronomy-grey/1.jpg" alt="Mason the astronomer" className="w-full aspect-[4/5] object-cover mt-8" />
            <img src="/products/newcatalog/signature-backpack-harper-reader-teal/1.jpg" alt="Harper the reader" className="w-full aspect-[4/5] object-cover" />
            <img src="/products/newcatalog/signature-backpack-stem-level-up-black/1.jpg" alt="STEM Level Up" className="w-full aspect-[4/5] object-cover mt-8" />
          </div>
        </div>
      </section>

      {/* LANGSTON PARTNERSHIP */}
      <section className="container-x py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="aspect-[5/6] overflow-hidden">
            <img src="/products/newcatalog/langston-travel-bag-blue/1.jpg" alt="Langston University collection" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">Officially Licensed</div>
            <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight">
              Langston <span className="font-black">University.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#4A4A4A] font-light leading-relaxed">
              We are the <strong className="text-black font-semibold">officially licensed collegiate vendor for Langston University</strong> -
              Oklahoma's only HBCU. Every piece in the Langston Legacy collection is designed and produced with the university's blessing.
            </p>
            <p className="mt-4 text-base md:text-lg text-[#4A4A4A] font-light leading-relaxed">
              Est. 1897 · One State. One HBCU. One Legacy.
            </p>
            <Link to="/shop?category=hbcu" className="mt-8 inline-flex btn-primary">Shop Langston <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FAFAF7] py-20 text-center">
        <div className="container-x max-w-2xl">
          <MakebaLogo height={40} tagline={true} className="mx-auto" />
          <h2 className="mt-6 font-display font-light text-3xl md:text-4xl tracking-tight">Let's build something together.</h2>
          <p className="mt-3 text-[#4A4A4A] font-light">From 10 shirts for your team to a nationwide campaign - we bring the same care to every project.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-primary">Request a Quote</Link>
            <Link to="/designer" className="btn-outline">Open Design Studio</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
